import type React from 'react';
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Music, LogIn, ArrowRight, X } from 'lucide-react';
import { useUser } from '@showliner/shared/context/UserContext';
import claimApi, { type PublicClaimRecord } from '@showliner/shared/services/claimApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { getAdminUrl } from '@/utils/crossAppLinks';
import { usePageTitle } from '@/hooks/usePageTitle';

type InvitationRecord = PublicClaimRecord & { performerName?: string };

const PerformerInvitationClaim: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useUser();

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<InvitationRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  usePageTitle('Claim your profile');

  useEffect(() => {
    const load = async () => {
      if (!token) { setError('That invitation link doesn\'t look right.'); setLoading(false); return; }
      try {
        setLoading(true);
        const data = await claimApi.getByToken(token);
        setInvitation({ ...data, performerName: data.entityType });
      } catch {
        setError('This invitation has expired or was already used.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleClaim = async () => {
    if (!token) return;
    try {
      setSubmitting(true);
      await claimApi.accept(token);
      toast.success('This profile is yours now. Taking you to your console…');
      // Session is in the `showliner_session` cookie on `.showliner.io`,
      // so admin will see the user as logged-in without any URL handoff.
      // See PR2 of docs/plans/AUTH_COOKIE_MIGRATION.md.
      window.location.href = getAdminUrl('/dashboard');
    } catch {
      setError('Couldn\'t claim this profile. Give it a minute and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!token) return;
    try {
      setSubmitting(true);
      await claimApi.decline(token);
      toast.info('Invitation declined');
      navigate('/');
    } catch {
      setError('Couldn\'t decline the invitation. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const goToLogin = () => {
    localStorage.setItem('auth_redirect', `/invite/${token}`);
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 space-y-4">
            <Skeleton className="h-6 w-48 mx-auto" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="p-8">
            <X className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">Invitation not found</h2>
            <p className="text-sm text-muted-foreground mb-6">{error}</p>
            <Button variant="outline" onClick={() => navigate('/')}>Go home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invitation) return null;

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Badge variant="secondary" className="mx-auto mb-2">
            <Music className="mr-1 h-3 w-3" /> Performer invitation
          </Badge>
          <CardTitle className="font-heading text-xl">{invitation.performerName}</CardTitle>
          <CardDescription>
            You've been invited to manage this performer profile on Showliner
          </CardDescription>
          {invitation.expiresAt && (
            <p className="text-xs text-muted-foreground mt-1">
              Expires {new Date(invitation.expiresAt).toLocaleDateString()}
            </p>
          )}
        </CardHeader>

        <CardContent>
          {error && (
            <div className="mb-4 p-3 rounded-md bg-destructive/10 text-destructive text-sm">{error}</div>
          )}

          {isAuthenticated ? (
            <div className="space-y-3">
              <Button className="w-full" onClick={handleClaim} disabled={submitting}>
                {submitting ? 'Claiming…' : 'Claim this profile'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={handleDecline}
                disabled={submitting}
              >
                Decline
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                Sign in or create an account to claim this performer profile.
              </p>
              <Button className="w-full" onClick={goToLogin}>
                <LogIn className="mr-2 h-4 w-4" /> Sign in to claim
              </Button>
              <Button variant="outline" className="w-full" asChild>
                <Link to={`/register?redirect=/invite/${token}`}>
                  Create an account
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformerInvitationClaim;
